import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Button,
  Card,
  FormField,
  Input,
  Logo,
  Select,
  useToast,
} from "@chotca/ui";
import {
  vi,
  createOrgRequestSchema,
  VN_PHONE_REGEX,
  BUSINESS_TYPES,
  DEFAULT_BUSINESS_TYPE,
  businessTypeOptions,
  type OrgLegalType,
} from "@chotca/config";
import { EmployerAnalyticsEvent } from "@chotca/config";
import { useCreateOrg, useLogout, useMe } from "@chotca/api-client";
import { logAnalyticsEvent } from "@/lib/analytics";
import { setAccessToken } from "@/boot/access-token";
import { Scribble } from "@/components/marketing/scribble";
import { AddressPicker, type AddressValue } from "@/components/address-picker";

const EMPTY_ADDRESS: AddressValue = {
  address: "",
  wardCode: "",
  wardName: "",
  wardProvinceCode: "",
  district: "",
  city: "",
  provinceCode: null,
  provinceName: null,
  lat: null,
  lng: null,
  mapUrl: null,
};

function addressReady(a: AddressValue): boolean {
  return (
    !!a.address &&
    a.lat !== null &&
    a.lng !== null &&
    // Ward is mandatory now (province + phường/xã), district is legacy.
    !!a.wardProvinceCode &&
    !!a.wardCode
  );
}

// The 3 legal types shown at onboarding step 1 (SCRUM-73, spec §5.1).
const LEGAL_TYPE_OPTIONS: {
  value: OrgLegalType;
  title: string;
  desc: string;
}[] = [
  {
    value: "INDIVIDUAL",
    title: "Tôi bán cá nhân, chưa có giấy phép kinh doanh",
    desc: "Quán nhỏ, bán tại nhà, xe đẩy, mới mở",
  },
  {
    value: "HOUSEHOLD",
    title: "Hộ kinh doanh cá thể",
    desc: "Có Giấy chứng nhận đăng ký hộ kinh doanh",
  },
  {
    value: "COMPANY",
    title: "Công ty (TNHH, Cổ phần)",
    desc: "Có Giấy chứng nhận đăng ký doanh nghiệp",
  },
];

const createOrgFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Vui lòng nhập tên thương hiệu")
    .max(120, "Tên thương hiệu tối đa 120 ký tự"),
  businessType: z.enum(BUSINESS_TYPES),
  // Legal-entity fields (HOUSEHOLD / COMPANY only). Optional — the employer can
  // "skip, fill later"; left blank for INDIVIDUAL.
  legalName: z.string().trim().max(200).optional(),
  legalRegNo: z.string().trim().max(50).optional(),
  contactName: z
    .string()
    .trim()
    .min(1, "Vui lòng nhập người liên hệ")
    .max(120, "Người liên hệ tối đa 120 ký tự"),
  contactPhone: z
    .string()
    .trim()
    .regex(
      VN_PHONE_REGEX,
      "SĐT không hợp lệ. Nhập số Việt Nam bắt đầu bằng 0 hoặc +84.",
    ),
  referredByCode: z.string().trim().max(12, "Mã giới thiệu tối đa 12 ký tự"),
  address: z
    .custom<AddressValue>()
    .refine(
      addressReady,
      "Vui lòng dán link Google Maps để lấy địa chỉ chính xác.",
    ),
});

type CreateOrgFormValues = z.infer<typeof createOrgFormSchema>;

export function CreateOrgForm() {
  const nav = useNavigate();
  const { error: toastError } = useToast();
  const createOrg = useCreateOrg();
  const me = useMe();
  const logout = useLogout({
    redirectTo: "/login",
    onCleared: () => setAccessToken(null),
  });
  const [error, setError] = React.useState<string | null>(null);
  const [showReferral, setShowReferral] = React.useState(false);
  // Onboarding is 2 steps now: (1) pick legal type, (2) fill the brand form.
  const [legalType, setLegalType] = React.useState<OrgLegalType | null>(null);
  const [step, setStep] = React.useState<1 | 2>(1);
  const needsLegalDocs = legalType === "HOUSEHOLD" || legalType === "COMPANY";

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateOrgFormValues>({
    resolver: zodResolver(createOrgFormSchema),
    mode: "onTouched",
    defaultValues: {
      name: "",
      businessType: DEFAULT_BUSINESS_TYPE,
      legalName: "",
      legalRegNo: "",
      contactName: "",
      contactPhone: "",
      referredByCode: "",
      address: EMPTY_ADDRESS,
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    setError(null);
    const a = values.address;
    try {
      // For HOUSEHOLD/COMPANY the business-registration number doubles as the
      // tax code, so we mirror legalRegNo into taxCode. INDIVIDUAL has neither.
      const legalRegNo = needsLegalDocs ? values.legalRegNo || null : null;
      const parsed = createOrgRequestSchema.parse({
        name: values.name,
        businessType: values.businessType,
        taxCode: legalRegNo,
        legalType: legalType ?? "INDIVIDUAL",
        legalName: needsLegalDocs ? values.legalName || null : null,
        legalRegNo,
        contactName: values.contactName,
        contactPhone: values.contactPhone,
        primaryAddress: {
          address: a.address,
          district: a.district,
          city: a.city,
          provinceCode: a.provinceCode!,
          wardCode: a.wardCode,
          wardName: a.wardName,
          wardProvinceCode: a.wardProvinceCode,
          lat: a.lat ?? 10.7769,
          lng: a.lng ?? 106.7009,
          mapUrl: a.mapUrl,
        },
        referredByCode: values.referredByCode.toUpperCase() || undefined,
      });
      // mutateAsync resolves only after onSuccess finishes refetching `me`,
      // so by the time we navigate the AuthGate sees the new membership.
      await createOrg.mutateAsync(parsed);
      logAnalyticsEvent(EmployerAnalyticsEvent.orgCreated, {
        venue_type: values.businessType,
        has_referral: !!values.referredByCode,
      });
      nav("/dashboard");
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Không tạo được thương hiệu";
      setError(msg);
      toastError(msg);
    }
  });

  return (
    <main className="min-h-screen bg-paper text-ink">
      <header className="border-b border-smoke">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between gap-4 px-6">
          <Link to="/" aria-label={vi.common.appName}>
            <Logo variant="horizontal" size={26} />
          </Link>
          <div className="flex items-center gap-3">
            {me.data?.email ? (
              <span className="hidden text-sm text-ink/55 sm:inline">
                {me.data.email}
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => void logout()}
              className="text-sm font-bold text-ink/70 underline-offset-4 hover:text-ink hover:underline"
            >
              Đăng xuất
            </button>
          </div>
        </div>
      </header>

      {step === 1 ? (
        <div className="mx-auto max-w-2xl px-6 py-12 lg:py-16">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest">
            <span className="text-ash/70">Tài khoản</span>
            <span className="text-ash/40">→</span>
            <span className="rounded-full bg-ink px-2.5 py-1 text-paper">
              Bước 1 / 2
            </span>
            <span className="text-ink">Loại hình</span>
          </div>

          <h1 className="mt-5 font-satoshi text-4xl font-black leading-[0.96] tracking-tight md:text-5xl">
            Quán của bạn{" "}
            <Scribble tone="lime-dark">đăng ký kinh doanh thế nào?</Scribble>
          </h1>
          <p className="mt-4 max-w-xl text-base text-ink/70">
            Chọn sai cũng không sao, đổi được sau. Loại hình không ảnh hưởng tới
            việc đăng tin.
          </p>

          <div className="mt-8 space-y-3">
            {LEGAL_TYPE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setLegalType(opt.value)}
                className={[
                  "flex w-full items-start gap-3 rounded-2xl border-2 p-4 text-left transition",
                  legalType === opt.value
                    ? "border-ink bg-lime/10"
                    : "border-smoke bg-paper hover:border-ink/40",
                ].join(" ")}
              >
                <span
                  className={[
                    "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2",
                    legalType === opt.value
                      ? "border-ink bg-ink"
                      : "border-smoke",
                  ].join(" ")}
                >
                  {legalType === opt.value ? (
                    <span className="h-2 w-2 rounded-full bg-paper" />
                  ) : null}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-bold text-ink">
                    {opt.title}
                  </span>
                  <span className="block text-sm text-ink/60">{opt.desc}</span>
                </span>
              </button>
            ))}
          </div>

          <div className="mt-8">
            <Button
              type="button"
              size="lg"
              disabled={!legalType}
              onClick={() => setStep(2)}
            >
              Tiếp tục
            </Button>
          </div>
        </div>
      ) : (
        <div className="mx-auto max-w-5xl px-6 py-12 lg:py-16">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="text-ash/70 underline-offset-4 hover:text-ink hover:underline"
            >
              Loại hình
            </button>
            <span className="text-ash/40">→</span>
            <span className="rounded-full bg-ink px-2.5 py-1 text-paper">
              Bước 2 / 2
            </span>
            <span className="text-ink">Thương hiệu</span>
          </div>

          <h1 className="mt-5 font-satoshi text-4xl font-black leading-[0.96] tracking-tight md:text-5xl">
            Thương hiệu của bạn,{" "}
            <Scribble tone="lime-dark">vài thông tin cơ bản.</Scribble>
          </h1>
          <p className="mt-4 max-w-xl text-base text-ink/70">
            Điền nhanh để ứng viên thấy bạn đáng tin. Mọi thông tin có thể chỉnh
            sửa sau trong phần cài đặt.
          </p>

          <div className="mt-8 grid items-start gap-8 lg:grid-cols-[1.5fr_1fr]">
            <form
              onSubmit={onSubmit}
              className="rounded-3xl border border-smoke bg-paper p-7 lg:p-9"
            >
              <FormField label="Tên thương hiệu" error={errors.name?.message}>
                <Input
                  maxLength={120}
                  placeholder="Cafe Bamboo Q.1"
                  autoFocus
                  invalid={!!errors.name}
                  {...register("name")}
                />
              </FormField>

              <div className="mt-5">
                <FormField label="Loại hình kinh doanh">
                  <Controller
                    control={control}
                    name="businessType"
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        options={businessTypeOptions()}
                      />
                    )}
                  />
                </FormField>
              </div>

              {needsLegalDocs ? (
                <Card className="mt-5 space-y-4 bg-mist/40 p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-bold text-ink">
                      Thông tin đăng ký kinh doanh
                    </p>
                    <span className="text-xs text-ash">Có thể điền sau</span>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <FormField label="Tên đăng ký kinh doanh">
                      <Input
                        maxLength={200}
                        placeholder="HKD Cà phê Bamboo"
                        {...register("legalName")}
                      />
                    </FormField>
                    <FormField label="Số ĐKKD / MST">
                      <Input
                        maxLength={50}
                        placeholder="0312345678"
                        {...register("legalRegNo")}
                      />
                    </FormField>
                  </div>
                </Card>
              ) : null}

              <div className="mt-5">
                <Controller
                  control={control}
                  name="address"
                  render={({ field }) => (
                    <AddressPicker
                      label="Địa chỉ thương hiệu"
                      description="Dán link Google Maps để lấy đúng toạ độ, ứng viên sẽ thấy khoảng cách chính xác đến quán."
                      required
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.address?.message as string | undefined}
                    />
                  )}
                />
              </div>

              <div className="mt-5 grid gap-5 md:grid-cols-2">
                <FormField
                  label="Người liên hệ"
                  error={errors.contactName?.message}
                >
                  <Input
                    placeholder="Anh A"
                    invalid={!!errors.contactName}
                    {...register("contactName")}
                  />
                </FormField>
                <FormField
                  label="SĐT liên hệ"
                  error={errors.contactPhone?.message}
                >
                  <Input
                    placeholder="0901 234 567"
                    invalid={!!errors.contactPhone}
                    {...register("contactPhone")}
                  />
                </FormField>
              </div>

              <div className="mt-5">
                {showReferral ? (
                  <FormField
                    label="Mã giới thiệu"
                    optional
                    description="Bạn được giới thiệu? Nhập mã của thương hiệu đã mời bạn, cả hai sẽ nhận thêm 3 lượt phỏng vấn khi bạn hoàn tất xác minh."
                    error={errors.referredByCode?.message}
                  >
                    <Input
                      maxLength={12}
                      placeholder="VD: AB7K2X9P"
                      style={{ textTransform: "uppercase" }}
                      invalid={!!errors.referredByCode}
                      {...register("referredByCode")}
                    />
                  </FormField>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowReferral(true)}
                    className="text-sm font-bold text-ink underline-offset-4 hover:underline"
                  >
                    + Mã giới thiệu (nếu có)
                  </button>
                )}
              </div>

              {error ? (
                <p className="mt-5 rounded-xl bg-danger/10 p-3 text-sm text-danger">
                  {error}
                </p>
              ) : null}

              <div className="mt-7">
                <Button type="submit" loading={createOrg.isPending} size="lg">
                  Tạo thương hiệu
                </Button>
              </div>
            </form>

            <aside className="hidden flex-col gap-3 lg:flex">
              <div className="rounded-3xl bg-lime p-7">
                <p className="font-mono text-[11px] font-bold uppercase tracking-widest text-ink/60">
                  {vi.landing.proofMainEyebrow}
                </p>
                <h3 className="mt-3 font-satoshi text-2xl font-black leading-tight tracking-tight">
                  {vi.landing.proofMainTitle}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-ink/80">
                  Ứng viên tin tưởng những quán có thông tin đầy đủ — tỉ lệ chốt
                  ca cao hơn 2.4×.
                </p>
              </div>
            </aside>
          </div>
        </div>
      )}
    </main>
  );
}
